import { FC, useContext } from "react";

import { Form, SubmitButton, useForm } from "capitalrx-components-2.0/lib/forms";
import { Button, useAlert } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";

import { getProviderManagementDocumentsUrlAPI, getProviderManagementAPI, updateProviderManagementAPI } from "@/api";
import { ProviderManagement } from "@/api/interfaces";
import { DetailsGroupFields } from "@/components/Account/Update/components/Details";
import { checkSamePhysicalAddress, ProviderDetailsDataForm } from "@/components/Account/Update/components/Details/DetailsGroupFields";
import { formatValueTin } from "@/components/Account/TinField";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { processDocumentFiles, uploadFileToS3, getThemeButton } from "@/utils";



type DetailsEditProps = {
  provider: ProviderManagement.ProviderSchema;
  documents: ProviderManagement.DocumentSchema[];
  setDocuments: React.Dispatch<React.SetStateAction<ProviderManagement.DocumentSchema[]>>;
  setProvider: (p: ProviderManagement.ProviderSchema) => void;
  setIsEditDetails: (v: boolean) => void;
  onCancel: () => void;
};

export const DetailsEdit: FC<DetailsEditProps> = ({ provider, documents, setDocuments, setProvider, setIsEditDetails, onCancel }) => {
  const { getAuthToken } = useContext(AuthenticationContext);
  const showAlert = useAlert();
  const { primary_color } = useContext(ThemeSelectorContext);

  const defaultValues: ProviderDetailsDataForm = {
    provider_type: provider.type,
    tin: formatValueTin(provider.tax_id_number, provider.tax_id_number_designation),
    designation: provider.tax_id_number_designation,
    npi: provider.billing_npi,
    medicaid_id_number: provider.medicaid_id_number,
    medicare_id_number: provider.medicare_id_number,
    legal_business_name: provider.legal_business_name,
    doing_business_name: provider.doing_business_as_name,
    ncpdp_number: provider.pharmacy_nabp_id,
    relationship_id: provider.pharmacy_chain_code,
    physical: {
      street_address: provider.physical_address_line_1,
      address_line: provider.physical_address_line_2,
      city: provider.physical_address_city,
      state: provider.physical_address_state,
      zip_code: provider.physical_address_zip_code,
    },
    mailing: {
      street_address: provider.mailing_address_line_1,
      address_line: provider.mailing_address_line_2,
      city: provider.mailing_address_city,
      state: provider.mailing_address_state,
      zip_code: provider.mailing_address_zip_code,
      check_same_physical_address: checkSamePhysicalAddress(provider),
    },
    w9_form: { files: [], notes: "" },
  };

  const form = useForm<ProviderDetailsDataForm>({ defaultValues });

  const uploadW9Form = async (w9_form: ProviderDetailsDataForm["w9_form"]) => {
    const { files, notes } = w9_form;

    if (files.length === 0) {
      showAlert({ type: "error", style: "banner", message: "Please select at least one file to upload" });
      return false;
    }

    const w9Result = processDocumentFiles(files, notes, "w9");
    const response = await getProviderManagementDocumentsUrlAPI({ files: w9Result.fileMetadata }, getAuthToken());

    if (!response.successful_files || response.successful_files.length === 0) {
      const errorMessage = response.errors?.map((err) => err.message).join("; ") || "No files were successfully processed";
      showAlert({ type: "error", style: "banner", message: errorMessage });
      return false;
    }

    const fileMap = new Map<string, File>();
    w9Result.fileObjects.forEach((obj) => fileMap.set(obj.fileName, obj.file));

    let successfulUploads = 0;
    const totalFilesToUpload = response.successful_files.length;

    for (const fileResult of response.successful_files) {
      try {
        const file = fileMap.get(fileResult.file_name);
        if (file && fileResult.post) {
          await uploadFileToS3(file, fileResult.post);
          successfulUploads++;
        }
      } catch (error) {
        console.error(`Error uploading file ${fileResult.file_name}:`, error);
      }
    }

    const mapped = response.successful_files.map((f: any) => ({
      id: typeof f.id === "number" ? f.id : Number(f.provider_document_id ?? Date.now()),
      provider_document_id: f.provider_document_id ?? f.id,
      bucket: f.bucket,
      key: f.key,
      file_name: f.file_name,
      file_size: f.file_size,
      content_type: f.content_type ?? "application/pdf",
      document_type: "w9",
      created_at: f.start_date ?? new Date().toISOString(),
      notes: f.notes ?? "",
    })) as ProviderManagement.DocumentSchema[];

    setDocuments((prev) => prev.concat(mapped));

    try {
      const latest = await getProviderManagementAPI(getAuthToken());
      setDocuments(latest.documents ?? []);
    } catch (e) {
      console.error("Failed to refresh documents after upload", e);
    }

    // Alerts
    if (successfulUploads === totalFilesToUpload) {
      showAlert({ type: "success", style: "banner", message: "Successfully uploaded W-9 form" });
      return true;
    } else if (successfulUploads > 0) {
      showAlert({
        type: "success",
        style: "banner",
        message: `${successfulUploads} of ${totalFilesToUpload} W-9 documents uploaded successfully`,
      });
      return true;
    } else {
      showAlert({ type: "error", style: "banner", message: "Failed to upload W-9 documents" });
      return false;
    }
  };

  const submitHandler = async (data: ProviderDetailsDataForm): Promise<void> => {
    logEvent(ANALYTIC_EVENTS.PROVIDER_MANAGEMENT.update);

    try {
      const hasNewW9File = Array.isArray(data.w9_form?.files) && data.w9_form.files.length > 0;
      if (hasNewW9File) {
        const w9UploadSuccess = await uploadW9Form(data.w9_form);
        if (!w9UploadSuccess) return;
      }
    } catch (error) {
      console.error(error);
      showAlert({ type: "error", style: "banner", message: "Failed to save W-9 form. Please try again later." });
      logEvent(ANALYTIC_EVENTS.ERRORS.providerManagementUpdate);
      return;
    }

    try {
      const payload = {
        medicare_id_number: data.medicare_id_number,
        medicaid_id_number: data.medicaid_id_number,
        legal_business_name: data.legal_business_name,
        doing_business_as_name: data.doing_business_name,
        physical_address_line_2: data.physical.address_line,
        physical_address_line_1: data.physical.street_address,
        physical_address_city: data.physical.city,
        physical_address_state: data.physical.state,
        physical_address_zip_code: data.physical.zip_code,
        mailing_address_line_2: data.mailing.address_line,
        mailing_address_line_1: data.mailing.street_address,
        mailing_address_city: data.mailing.city,
        mailing_address_state: data.mailing.state,
        mailing_address_zip_code: data.mailing.zip_code,
      };
      await updateProviderManagementAPI(payload, getAuthToken());
      setProvider({ ...provider, ...payload });
      setIsEditDetails(false);
      showAlert({ type: "success", style: "banner", message: "Successfully submitted provider details" });
    } catch (error) {
      console.error(error);
      showAlert({ type: "error", style: "banner", message: "Failed to save provider details. Please try again later." });
      logEvent(ANALYTIC_EVENTS.ERRORS.providerManagementUpdate);
      return;
    }
    logEvent(ANALYTIC_EVENTS.PROVIDER_MANAGEMENT.update_success);
  };

  return (
    <Form form={form} onSubmit={submitHandler}>
      <Stack spacing="large">
        <DetailsGroupFields readOnly={false} provider={provider} documents={documents} />
        <Shelf spacing="small">
          <SubmitButton appearance={getThemeButton("primary", primary_color)}>Save</SubmitButton>
          <Button appearance={getThemeButton("secondary", primary_color)} onClick={onCancel}>Cancel</Button>
          <Shelf.Spacer />
        </Shelf>
      </Stack>
    </Form>
  );
};
